// src/components/Modals/EditProfileModal.js
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { X, FileText, AlertTriangle, User } from "lucide-react";
import Button from "../UI/Button";

/* ---------------- Avatar helpers (NO AI images) ---------------- */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
};

const isValidPhoto = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return false;
  if (s.includes("pravatar.cc")) return false;
  return true;
};

const ChildAvatar = ({ fullName, photoUrl, size = 48 }) => {
  const initials = getInitials(fullName);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [photoUrl]);

  const canShowPhoto = isValidPhoto(photoUrl) && !broken;

  if (canShowPhoto) {
    return (
      <img
        src={photoUrl}
        alt={fullName}
        className="rounded-full object-cover border border-gray-200 dark:border-gray-800 shrink-0"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    );
  }

  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0
                 border border-gray-200 dark:border-gray-800
                 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold"
      style={{ width: size, height: size }}
      title={fullName}
    >
      {initials || <User size={18} />}
    </div>
  );
};

/* ----------------- Dashboard-like Modal Shell ----------------- */
const ModalShell = ({ title, subtitle, onClose, children, maxWidth = "max-w-3xl" }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 flex items-center justify-center p-3 md:p-4">
      <div
        className={`w-full ${maxWidth} max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden flex flex-col transition-colors duration-300`}
      >
        <div className="px-5 md:px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p> : null}
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 transition-colors"
            type="button"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800" />

        <div className="px-5 md:px-6 py-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

/* ----------------- Small UI Helpers ----------------- */
const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 " +
      (props.className || "")
    }
  />
);

const Select = (props) => (
  <select
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 " +
      (props.className || "")
    }
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 " +
      "min-h-[110px] resize-none " +
      (props.className || "")
    }
  />
);

/* ----------------- Reintegration rule helpers ----------------- */
const isReintegratedPair = (status, adoptionStatus) => {
  const s = String(status || "").toLowerCase();
  const a = String(adoptionStatus || "").toLowerCase();
  return s === "reintegrated" && a === "adopted";
};

const shouldClearReintegration = (status, adoptionStatus) => {
  return !isReintegratedPair(status, adoptionStatus);
};

/* ===========================
   EditProfileModal (CLOUDINARY READY)
   - uses dateOfBirth instead of age
   =========================== */
const EditProfileModal = ({ child, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);

  const hasExistingReintegration = useMemo(() => {
    return !!(child?.reintegration || child?.reintegration_details);
  }, [child]);

  const [showReintegrationClearWarning, setShowReintegrationClearWarning] = useState(false);

  useEffect(() => {
    if (!child) return;

    setForm({
      firstName: child.firstName ?? child.first_name ?? "",
      middleName: child.middleName ?? child.middle_name ?? "",
      lastName: child.lastName ?? child.last_name ?? "",
      dateOfBirth: child.dateOfBirth ?? child.date_of_birth ?? "",
      gender: child.gender ?? "",
      admissionDate: child.admissionDate ?? child.admission_date ?? "",
      house: child.house ?? "",
      houseParent: child.houseParent ?? child.house_parent ?? "",
      address: child.address ?? "",
      healthStatus: child.healthStatus ?? child.health_status ?? "Good",
      educationLevel: child.educationLevel ?? child.education_level ?? "",
      emergencyContact: child.emergencyContact ?? child.emergency_contact ?? "",
      caseType: child.caseType ?? child.case_type ?? "",
      status: child.status ?? "Active",
      adoptionStatus: child.adoptionStatus ?? child.adoption_status ?? "Not Available for Adoption",
      notes: child.notes ?? "",
      lastCheckup: child.lastCheckup ?? child.last_checkup ?? "",
      photo: null,
    });

    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setShowReintegrationClearWarning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  if (!child || !form) return null;

  const update = (key, value) => {
    setForm((p) => {
      const next = { ...p, [key]: value };

      const status = key === "status" ? value : next.status;
      const adoptionStatus = key === "adoptionStatus" ? value : next.adoptionStatus;

      const willClear = hasExistingReintegration && shouldClearReintegration(status, adoptionStatus);
      setShowReintegrationClearWarning(willClear);

      return next;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;

    update("photo", file);

    if (preview) URL.revokeObjectURL(preview);
    setPreview(file ? URL.createObjectURL(file) : null);

    if (file) toast.success("New photo selected");
    else toast("Photo selection cleared");
  };

  const handleSave = async () => {
    if (!String(form.firstName || "").trim()) {
      toast.error("First name is required");
      return;
    }

    if (!String(form.lastName || "").trim()) {
      toast.error("Last name is required");
      return;
    }

    if (!String(form.dateOfBirth || "").trim()) {
      toast.error("Date of birth is required");
      return;
    }

    if (!String(form.gender || "").trim()) {
      toast.error("Gender is required");
      return;
    }

    if (!String(form.emergencyContact || "").trim()) {
      toast.error("Emergency contact is required");
      return;
    }

    const next = { ...child, ...form };

    if (shouldClearReintegration(next.status, next.adoptionStatus)) {
      next.reintegration = null;
      next.reintegration_details = null;
    }

    try {
      await onSave(next);
      onClose();
    } catch (e) {
      console.error("Save failed:", e);
      toast.error(e?.message || "Failed to save changes", { id: "edit-child" });
    }
  };

  const fullName = `${form.firstName} ${form.middleName ? form.middleName + " " : ""}${form.lastName}`.trim();

  const existingPhoto = child.photoUrl || child.photo_url || child.photo || null;
  const currentPhoto = preview || existingPhoto || null;

  return (
    <ModalShell title="Edit Child" subtitle={fullName} onClose={onClose} maxWidth="max-w-3xl">
      <div className="flex items-center gap-3 mb-5">
        <ChildAvatar fullName={fullName} photoUrl={currentPhoto} size={48} />

        <div className="min-w-0">
          <p className="text-sm text-gray-600 dark:text-gray-400">Editing profile for</p>
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{fullName}</p>
        </div>
      </div>

      {showReintegrationClearWarning ? (
        <div className="mb-5 p-4 rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-600/10 flex items-center justify-center shrink-0">
              <AlertTriangle size={18} className="text-amber-700 dark:text-amber-300" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold">Reintegration details will be cleared</p>
              <p className="text-sm mt-1 opacity-90">
                This child has existing reintegration data. Changing Status/Adoption Status away from
                <span className="font-medium"> Reintegrated + Adopted</span> will remove those details.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="First Name">
          <Input value={form.firstName} onChange={(e) => update("firstName", e.target.value)} required />
        </Field>

        <Field label="Middle Name">
          <Input value={form.middleName} onChange={(e) => update("middleName", e.target.value)} />
        </Field>

        <Field label="Last Name">
          <Input value={form.lastName} onChange={(e) => update("lastName", e.target.value)} required />
        </Field>

        <Field label="Date of Birth">
          <Input
            type="date"
            value={form.dateOfBirth}
            onChange={(e) => update("dateOfBirth", e.target.value)}
            required
          />
        </Field>

        <Field label="Gender">
          <Select value={form.gender} onChange={(e) => update("gender", e.target.value)} required>
            <option value="">Select gender</option>
            <option>Male</option>
            <option>Female</option>
            <option>Other</option>
          </Select>
        </Field>

        <Field label="Admission Date">
          <Input type="date" value={form.admissionDate} onChange={(e) => update("admissionDate", e.target.value)} />
        </Field>

        <Field label="Home">
          <Select value={form.house} onChange={(e) => update("house", e.target.value)} required>
            <option value="">Select Home</option>
            <option>Home 1</option>
            <option>Home 2</option>
            <option>Home 3</option>
            <option>Home 4</option>
          </Select>
        </Field>

        <Field label="House Parent">
          <Select value={form.houseParent} onChange={(e) => update("houseParent", e.target.value)} required>
            <option value="">Select House Parent</option>
              <option>Romeo & Lucia Torremocha</option>
              <option>Arnel & Grace Bermoy</option>
              <option>Greg & Geraldine Torremocha</option>
              <option>Mariel & Alvin Torremocha</option>
          </Select>
        </Field>

        <Field label="Health Status">
          <Select value={form.healthStatus} onChange={(e) => update("healthStatus", e.target.value)}>
            <option>Excellent</option>
            <option>Good</option>
            <option>Needs Check-up</option>
            <option>Requires Attention</option>
          </Select>
        </Field>

        <Field label="Education Level">
          <Input value={form.educationLevel} onChange={(e) => update("educationLevel", e.target.value)} required />
        </Field>

        <Field label="Emergency Contact">
          <Input
            type="tel"
            value={form.emergencyContact}
            onChange={(e) => update("emergencyContact", e.target.value.replace(/[^\d+]/g, ""))}
            placeholder="Enter contact number"
            required
          />
        </Field>

        <div className="md:col-span-3">
          <Field label="Address">
            <Textarea
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              placeholder="Enter child's address or place of origin"
            />
          </Field>
        </div>

        <Field label="Case Type">
          <Select value={form.caseType} onChange={(e) => update("caseType", e.target.value)} required>
            <option value="">Select case type</option>
            <option>Orphan</option>
            <option>Abandoned</option>
            <option>Surrender</option>
            <option>Neglected</option>
            <option>Abused</option>
            <option>Child Dependent</option>
            <option>Child at Risk</option>
            <option>VAWC</option>
          </Select>
        </Field>

        <Field label="Last Check-up">
          <Input type="date" value={form.lastCheckup} onChange={(e) => update("lastCheckup", e.target.value)} />
        </Field>

        <Field label="Status">
          <Select value={form.status} onChange={(e) => update("status", e.target.value)}>
            <option>Active</option>
            <option>Inactive</option>
            <option>Transitioning</option>
            <option>Transferred</option>
            <option>Reintegrated</option>
          </Select>
        </Field>

        <Field label="Adoption Status">
          <Select value={form.adoptionStatus} onChange={(e) => update("adoptionStatus", e.target.value)}>
            <option>Not Available for Adoption</option>
            <option>Open for Adoption</option>
            <option>Adopted</option>
          </Select>
        </Field>

        <div className="md:col-span-3">
          <Field label="Profile Image / Photo">
            <Input name="photo" type="file" accept="image/*" onChange={handleFileChange} />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Leave empty if you don’t want to change the photo.
            </p>
          </Field>
        </div>

        <div className="md:col-span-3">
          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-end gap-3">
        <Button type="button" variant="outline" size="medium" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" size="medium" onClick={handleSave} className="flex items-center gap-2">
          <FileText size={16} />
          Save Changes
        </Button>
      </div>
    </ModalShell>
  );
};

export default EditProfileModal;